import { css } from '@emotion/react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { fetchCompanyProfile, ISourcingTypeaheadResult } from '../../services/queries/SourcingCompanyQueries';
import {
  useDeleteSourcingList,
  useDuplicateListDialog,
  useEnrichListNow,
  useRefreshListNow,
  useRenameSourcingList,
} from './hooks/useSourcingListActions';
import { useToastMessageState } from '../../components/ToastMessage/ToastMessageProvider';
import { useContextMenu } from '../../components/ContextMenu/useContextMenu';
import { ConfirmDeletionDialog } from '../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { buildAddCompanyPayload, buildProfileLookup } from './util/buildAddCompanyPayload';
import { DuplicateListDialog } from './DuplicateListDialog';
import { HeaderActions } from './SourcingHeaderActions';
import { SourcingContent } from './SourcingContent';
import { SourcingHeader } from './SourcingHeader';
import { BulkUploadDialog } from './components/BulkUploadDialog';
import { SourcingMenu } from './components/SourcingMenu';
import { buildListMenuSections } from './util/buildListMenuSections';
import {
  useAddCompaniesToListMutation,
  useSourcingListsQuery,
} from '../../services/queries-hooks/SourcingListsQueriesHooks';
import {
  refreshSettingsDialogAtom,
  sourcingGridApiAtom,
  sourcingTitleEditingAtom,
} from './state/SourcingListsState';

const rootCss = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export function SourcingResultsPage() {
  const { listId, savedSearchId } = useParams<{
    listId?: string;
    savedSearchId?: string;
  }>();

  const { data: sourcingData } = useSourcingListsQuery();
  const setTitleEditing = useSetAtom(sourcingTitleEditingAtom);
  const { pushErrorToast } = useToastMessageState();

  const selectedList = useMemo(() => {
    const lists = sourcingData?.lists ?? [];
    const savedSearches = sourcingData?.savedSearches ?? [];
    if (listId) {
      return lists.find((l) => l.id === listId) ?? null;
    }
    if (savedSearchId) {
      return savedSearches.find((s) => s.id === savedSearchId) ?? null;
    }
    return null;
  }, [listId, savedSearchId, sourcingData]);

  // ── CSV export via grid API atom ────────────────────────────────────────
  const gridApi = useAtomValue(sourcingGridApiAtom);

  const handleDownloadCsv = useCallback(() => {
    const fileName = selectedList?.entityType === 'person' ? 'sourcing-people.csv' : 'sourcing-companies.csv';
    gridApi?.exportDataAsCsv({ fileName, allColumns: true });
  }, [gridApi, selectedList?.entityType]);

  // ── Context menu ──────────────────────────────────────────────────────────
  const { menuPos, handleMenuButtonClick, handleClose } = useContextMenu();

  // ── Delete dialog ─────────────────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const deleteList = useDeleteSourcingList();

  const renameList = useRenameSourcingList();

  const duplicateDialog = useDuplicateListDialog(selectedList?.id);
  // Destructure the stable refs the menu builder needs so useMemo isn't
  // invalidated on every render by the hook's fresh return object.
  const { isPending: isDuplicatePending, openDialog: openDuplicateDialog } = duplicateDialog;

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleSaveTitle = (newName: string) => {
    if (!selectedList || selectedList.listType === 'active') {
      return;
    }
    renameList.mutate({ id: selectedList.id, name: newName });
  };

  const handleDeleteConfirm = () => {
    if (!selectedList || selectedList.listType === 'active') {
      return;
    }
    deleteList.mutate(selectedList.id, {
      onSettled: () => {
        setDeleteDialogOpen(false);
      },
    });
  };

  // ── Enrich Now (static lists) ──────────────────────────────────────────────
  const enrichMutation = useEnrichListNow();

  const handleEnrichNow = useCallback(() => {
    if (!selectedList || selectedList.listType !== 'static') {
      return;
    }
    enrichMutation.mutate(selectedList);
  }, [selectedList, enrichMutation]);

  // ── Run Now (saved searches) ────────────────────────────────────────────
  const refreshMutation = useRefreshListNow();

  const handleRunNow = useCallback(() => {
    if (!selectedList || selectedList.listType !== 'active') {
      return;
    }
    refreshMutation.mutate(selectedList);
  }, [selectedList, refreshMutation]);

  // ── Bulk Upload (static lists) ──────────────────────────────────────────
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkUploadLaunched, setBulkUploadLaunched] = useState(false);
  const isBulkUploadLoading = bulkUploadDialogOpen && !bulkUploadLaunched;

  const handleBulkUpload = useCallback(() => {
    if (!selectedList || selectedList.listType !== 'static') {
      return;
    }
    setBulkUploadDialogOpen(true);
  }, [selectedList]);

  const handleBulkUploadClose = useCallback(() => {
    setBulkUploadDialogOpen(false);
    setBulkUploadLaunched(false);
  }, []);

  const handleBulkUploadLaunched = useCallback(() => setBulkUploadLaunched(true), []);

  // ── Add Company (static lists) ──────────────────────────────────────────
  const addCompaniesMutation = useAddCompaniesToListMutation(selectedList?.id);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);
  const isAddingCompany = isFetchingProfile || addCompaniesMutation.isPending;

  const handleAddCompany = useCallback(
    async (result: ISourcingTypeaheadResult) => {
      const lookup = buildProfileLookup(result);
      let profile = null;
      if (lookup) {
        setIsFetchingProfile(true);
        try {
          profile = await fetchCompanyProfile(lookup);
        } catch {
          // Profile lookup is best-effort — fall back to the typeahead basics.
        } finally {
          setIsFetchingProfile(false);
        }
      }
      const payload = buildAddCompanyPayload(result, profile);
      addCompaniesMutation.mutate([payload]);
    },
    [addCompaniesMutation]
  );

  // ── Menu sections ─────────────────────────────────────────────────────────
  const openRefreshSettings = useSetAtom(refreshSettingsDialogAtom);

  const menuSections = useMemo(
    () =>
      buildListMenuSections({
        list: selectedList,
        isDuplicating: isDuplicatePending,
        onRename: () => setTitleEditing({ isEditing: true, initialValue: selectedList?.name ?? '' }),
        onDuplicate: () => {
          if (!selectedList) {
            return;
          }
          handleClose();
          openDuplicateDialog();
        },
        onDelete: () => setDeleteDialogOpen(true),
        onOpenRefreshSettings: () => selectedList && openRefreshSettings(selectedList),
        onCopyLinkError: (message) => pushErrorToast({ message }),
        includeEnrichOrRunItem: false,
      }),
    [
      selectedList,
      setTitleEditing,
      pushErrorToast,
      isDuplicatePending,
      openDuplicateDialog,
      handleClose,
      openRefreshSettings,
    ]
  );

  return (
    <div css={rootCss}>
      <SourcingHeader selectedList={selectedList} onSaveTitle={handleSaveTitle}>
        <HeaderActions
          listType={selectedList?.listType ?? 'static'}
          entityType={selectedList?.entityType ?? 'company'}
          onDownloadCsv={handleDownloadCsv}
          onMenuButtonClick={handleMenuButtonClick}
          onEnrichNow={handleEnrichNow}
          isEnrichLoading={enrichMutation.isPending}
          onRunNow={handleRunNow}
          isRunLoading={refreshMutation.isPending}
          onBulkUpload={handleBulkUpload}
          isBulkUploadLoading={isBulkUploadLoading}
          onAddCompany={handleAddCompany}
          isAddingCompany={isAddingCompany}
        />
      </SourcingHeader>

      <SourcingContent
        key={selectedList?.id}
        listId={selectedList?.id}
        entityType={selectedList?.entityType ?? 'company'}
        listType={selectedList?.listType ?? 'static'}
      />

      <SourcingMenu sections={menuSections} menuPos={menuPos} onClose={handleClose} />

      <DuplicateListDialog
        open={duplicateDialog.open}
        loading={duplicateDialog.isPending}
        originalName={selectedList?.name ?? ''}
        existingNames={duplicateDialog.existingNames}
        serverError={duplicateDialog.serverError}
        onCancel={duplicateDialog.closeDialog}
        onConfirm={duplicateDialog.confirm}
      />

      {selectedList && selectedList.listType === 'static' && (
        <BulkUploadDialog
          open={bulkUploadDialogOpen}
          list={selectedList}
          onClose={handleBulkUploadClose}
          onLaunched={handleBulkUploadLaunched}
        />
      )}

      <ConfirmDeletionDialog
        open={deleteDialogOpen}
        title={`Delete "${selectedList?.name ?? ''}"?`}
        loading={deleteList.isPending}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
      >
        The list will be deleted permanently.
      </ConfirmDeletionDialog>
    </div>
  );
}
